import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generate a PDF report of menu items grouped by brand.
 * Columns: Item, Size, Selling Price, Food Cost, FC%.
 * Opens in a new browser tab.
 *
 * @param {Object} params
 * @param {Array} params.data - Menu items with recipe_lines, packaging, selling_prices (from fetchMenuItemDetail)
 */
export function generateMenuItemsPdfReport({ data }) {
  if (!data?.length) return { error: "No menu items to export." };

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ── Header ──────────────────────────────────────────────────────────────
  let y = margin;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Pizza Felice LLC", margin, y);
  doc.text("Menu Items by Brand", pageWidth / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.text("Internal", pageWidth - margin, y, { align: "right" });
  y += 6;
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`${data.length} items`, margin, y);
  doc.setTextColor(0, 0, 0);
  y += 5;

  /** Check if a line's dimensions match a given platform/brand/location */
  function dimMatches(line, platformId, brandId, locationId) {
    if (line.platform_id != null && line.platform_id !== platformId) return false;
    if (line.brand_id != null && line.brand_id !== brandId) return false;
    if (line.location_id != null && line.location_id !== locationId) return false;
    return true;
  }

  // ── Flatten: one row per selling-price combo ───────────────────────────
  const rows = [];
  for (const item of data) {
    const prices = item.selling_prices?.length ? item.selling_prices : [];
    const recipeLines = item.recipe_lines?.length ? item.recipe_lines : [];

    if (!prices.length) {
      rows.push({
        name: item.name,
        size: item.size || "",
        brand: "-",
        price: "-",
        foodCost: item.food_cost != null ? Number(item.food_cost) : null,
        fcPct: null,
      });
    } else {
      for (const sp of prices) {
        // Sum recipe line costs matching this price's dimensions
        // API recipe_lines: quantity = batch multiplier, recipe_cost = full batch cost
        let recipeCost = 0;
        for (const rl of recipeLines) {
          if (!rl.recipe_id) continue;
          if (!dimMatches(rl, sp.platform_id, sp.brand_id, sp.location_id)) continue;
          recipeCost += Number(rl.quantity || 0) * Number(rl.recipe_cost || 0);
        }
        // Sum packaging costs matching this price's dimensions
        let pkgCost = 0;
        for (const p of (item.packaging || [])) {
          if (!p.packaging_id || !p.quantity) continue;
          if (!dimMatches(p, sp.platform_id, sp.brand_id, sp.location_id)) continue;
          pkgCost += Number(p.quantity) * Number(p.price_per_unit || 0);
        }
        const fc = recipeCost + pkgCost;
        const fcPct = sp.price && fc ? Math.round((fc / Number(sp.price)) * 1000) / 10 : null;
        rows.push({
          name: item.name,
          size: item.size || "",
          brand: sp.brand_name || "All",
          price: sp.price != null ? Number(sp.price) : null,
          foodCost: fc,
          fcPct,
        });
      }
    }
  }

  // ── Group rows by brand ─────────────────────────────────────────────────
  const grouped = {};
  for (const row of rows) {
    const brand = row.brand;
    if (!grouped[brand]) grouped[brand] = [];
    grouped[brand].push(row);
  }
  const brandOrder = Object.keys(grouped).sort((a, b) => {
    if (a === "-" || a === "All") return 1;
    if (b === "-" || b === "All") return -1;
    return a.localeCompare(b);
  });

  // ── Build table body ───────────────────────────────────────────────────
  const body = [];
  for (const brand of brandOrder) {
    body.push([
      { content: brand, colSpan: 5, styles: { fontStyle: "bold", fillColor: [240, 240, 240], textColor: [50, 50, 50] } },
    ]);
    for (const row of grouped[brand]) {
      body.push([
        row.name,
        row.size,
        row.price != null ? fmtAmt(row.price) : "-",
        row.foodCost != null ? fmtAmt(row.foodCost) : "-",
        row.fcPct != null ? `${row.fcPct}%` : "-",
      ]);
    }
  }

  // ── Render table ───────────────────────────────────────────────────────
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Item", "Size", "Selling Price", "Food Cost", "FC %"]],
    body,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 52 },
      1: { cellWidth: 20, halign: "center" },
      2: { cellWidth: 26, halign: "right" },
      3: { cellWidth: 26, halign: "right" },
      4: { cellWidth: 22, halign: "right" },
    },
  });

  // ── Footer ─────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${dateStr} at ${timeStr}`, margin, pageHeight - 6);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: "right" });
  }

  doc.setTextColor(0, 0, 0);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");

  return { success: true };
}

/** Format currency amount — always 2 decimal places */
function fmtAmt(val) {
  const n = Number(val || 0);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
